import { useCallback, useMemo } from 'react';

export type StringFlatFacetValue = {
  value: string;
  state: string;
  numberOfResults: number;
};

export type NumericFlatFacetValue = {
  start: number;
  end: number;
  endInclusive: boolean;
  state: string;
  numberOfResults: number;
};

export type DateFlatFacetValue = {
  start: string;
  end: string;
  endInclusive: boolean;
  state: string;
  numberOfResults: number;
};

type FacetLikeValue = { state: string; numberOfResults: number };

type FacetLike<TValue extends FacetLikeValue> = {
  state: { values: TValue[] };
  methods?: {
    toggleSelect: (value: TValue) => void;
    deselectAll: () => void;
  };
};

/**
 * Thin adapter over a generated facet hook (controllers.useXFacet) for the flat
 * (non-hierarchical) facets. Computes the selected-id set + count for
 * TreeAccordion and wires clear/toggle to the controller methods.
 *
 * Replaced the retired CSR path's getController + useController +
 * useStableFacetValues + useOptimisticFacetSelection stack, which existed to paper over the old
 * module-singleton engine's remount jank. With the headless-react controller:
 *  - the controller is stable (no WeakMap cache),
 *  - Coveo flips each value's `state` to 'selected' synchronously on
 *    toggleSelect (no optimistic layer needed), and
 *  - values come straight from controller state (no stabilization store).
 *
 * `methods` is undefined under the StaticStateProvider (server render), hence
 * the optional calls. `isReady` exposes that window so the components can
 * render disabled instead of swallowing clicks — including permanently, when
 * hydrateStaticState fails and methods never arrives.
 */
export function useFlatFacet<TValue extends FacetLikeValue>({
  facet,
  getValueId,
}: {
  facet: FacetLike<TValue>;
  getValueId: (value: TValue) => string;
}) {
  const values = facet.state.values;
  const methods = facet.methods;

  const selectedIds = useMemo(
    () => values.filter((v) => v.state === 'selected').map(getValueId),
    [values, getValueId]
  );

  const handleClear = useCallback(() => {
    methods?.deselectAll();
  }, [methods]);

  const handleSelectMany = useCallback(
    ([id]: string[]) => {
      const value = values.find((v) => getValueId(v) === id);
      if (value) methods?.toggleSelect(value);
    },
    [values, getValueId, methods]
  );

  return {
    values,
    selectedIds,
    selectedCount: selectedIds.length,
    isReady: methods !== undefined,
    handleClear,
    handleSelectMany,
  };
}
